import React, { useState, useEffect, useCallback } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { Tabs, TabList, TabPanel } from "react-tabs";
import PanelHeaderContainer from "../../components/structure/Panels/PanelHeaderContainer";
import PanelIconTab from "../../components/structure/Panels/PanelIconTab";
import UserContainer from "../../state/containers/UserContainer";
import OrganizationContainer from "../../state/containers/OrganizationContainer";
import RevenueContainer from "../../state/containers/RevenueContainer";
import AuthContainer from "../../state/containers/AuthContainer";
import MonthSelector from "./components/MonthSelector";
import OrganizationSelector from "./components/OrganizationSelector";
import OrganizationSection from "./components/OrganizationSection";
import ProjectionsTab from "./components/ProjectionsTab";
import { BillingConfig, ContractType, CONTRACT_TYPES, OrganizationConfig } from "../../models/revenue/BillingConfig";
import Loader from "../../components/general/Loader";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_dark,
    color_shades_light,
    color_shades_lightest,
    color_shades_darker,
} from "../../constants/colors";

const revenueDark = require("../../content/images/revenue/revenue-dark.svg");
const revenueBlue = require("../../content/images/revenue/revenue-blue.svg");
const projectionsDark = require("../../content/images/revenue/projections-dark.svg");
const projectionsBlue = require("../../content/images/revenue/projections-blue.svg");

interface RevenueSceneProps {
    userContainer: UserContainer;
    organizationContainer: OrganizationContainer;
    revenueContainer: RevenueContainer;
    authContainer: AuthContainer;
}

const Revenue: React.FC<RevenueSceneProps> = ({
    userContainer,
    organizationContainer,
    revenueContainer,
    authContainer,
}) => {
    // Auth guard: must be logged in with @voicify.com email
    const currentUser = userContainer.state.currentUser;
    if (currentUser && currentUser.email) {
        const emailLower = currentUser.email.toLowerCase();
        if (!emailLower.endsWith("@voicify.com")) {
            return <Redirect to="/v" />;
        }
    }

    const history = useHistory();
    const routeLocation = useLocation();

    const selectedTabIndex = routeLocation.pathname.indexOf("projections") > -1 ? 1 : 0;

    const handleTabSelection = (index: number, last: number) => {
        if (index === last) return;
        if (index === 0) {
            history.push("/v/revenue" + window.location.search);
        }
        if (index === 1) {
            history.push("/v/revenue/projections" + window.location.search);
        }
    };

    const tabs = [
        { title: "Revenue", icon: revenueDark, selectedIcon: revenueBlue },
        { title: "Projections", icon: projectionsDark, selectedIcon: projectionsBlue },
    ];

    const [showSettings, setShowSettings] = useState(false);
    const [clipboardConfig, setClipboardConfig] = useState<BillingConfig | null>(null);
    const [collapsedPayingSections, setCollapsedPayingSections] = useState<Set<string>>(new Set());

    const togglePayingSection = (key: string) => {
        setCollapsedPayingSections(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Load config from API (with localStorage fallback) on mount, refresh token to avoid 401s
    useEffect(() => {
        const init = async () => {
            // Ensure we have a fresh access token before any API calls
            await authContainer.refreshToken();
            revenueContainer.loadConfig();
            organizationContainer.getOrganizations();
        };
        init();
    }, []);

    const handleAddOrg = useCallback(
        (orgId: string, orgName: string) => {
            revenueContainer.addOrganization(orgId, orgName);
        },
        [revenueContainer]
    );

    const handleRemoveOrg = useCallback(
        (orgId: string) => {
            revenueContainer.removeOrganization(orgId);
        },
        [revenueContainer]
    );

    const handleMonthChange = useCallback(
        (month: Date) => {
            revenueContainer.setSelectedMonth(month);
        },
        [revenueContainer]
    );

    const handleLoadData = useCallback(async () => {
        // Refresh token before loading data to avoid 401 errors
        await authContainer.refreshToken();
        revenueContainer.loadAllData();
    }, [revenueContainer, authContainer]);

    const handleForceReload = useCallback(async () => {
        await authContainer.refreshToken();
        revenueContainer.loadAllData(true);
    }, [revenueContainer, authContainer]);

    const handleRecalculate = useCallback(() => {
        revenueContainer.calculateAllRevenue();
    }, [revenueContainer]);

    const handleSaveConfig = useCallback(async () => {
        await revenueContainer.saveConfigToApi();
    }, [revenueContainer]);

    const handleCopyConfig = useCallback((config: BillingConfig) => {
        setClipboardConfig({ ...config });
    }, []);

    const handleExportCompleteConfig = useCallback(() => {
        try {
            const jsonString = JSON.stringify(revenueContainer.state.revenueConfig, null, 2);
            const blob = new Blob([jsonString], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `revenue-config-complete.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(`Failed to export config: ${e.message}`);
        }
    }, [revenueContainer]);

    const handleImportCompleteConfig = useCallback(() => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".json";
        input.onchange = (e: any) => {
            const file = e.target?.files?.[0];
            if (!file) return;
            
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const jsonString = event.target?.result as string;
                    const config = JSON.parse(jsonString);
                    
                    // Validate structure
                    if (!config.version || !config.organizations || !Array.isArray(config.organizations)) {
                        alert("Invalid configuration file format");
                        return;
                    }
                    
                    // Confirm before replacing
                    if (!confirm(`This will replace your current configuration with ${config.organizations.length} organizations. Continue?`)) {
                        return;
                    }
                    
                    // Update state and save
                    revenueContainer.setState({ 
                        ...revenueContainer.state, 
                        revenueConfig: config 
                    });
                    revenueContainer.saveConfig(config);
                    alert("Configuration imported successfully!");
                } catch (e) {
                    alert(`Failed to import config: ${e.message}`);
                }
            };
            reader.readAsText(file);
        };
        input.click();
    }, [revenueContainer]);

    const isLoading =
        revenueContainer.state.isLoadingEngagements ||
        revenueContainer.state.isLoadingApplications;

    const loadingStatus = (() => {
        if (!isLoading) return "";
        const totalOrgs = revenueContainer.state.revenueConfig.organizations.length;
        const loadingIds = revenueContainer.state.loadingOrgIds;
        const doneCount = totalOrgs - loadingIds.length;
        if (revenueContainer.state.isLoadingApplications && revenueContainer.state.isLoadingEngagements) {
            return `Loading conversations... ${doneCount}/${totalOrgs} orgs`;
        }
        if (revenueContainer.state.isLoadingApplications) {
            return `Loading applications... (${totalOrgs} orgs)`;
        }
        if (revenueContainer.state.isLoadingEngagements) {
            return `Loading conversations... ${doneCount}/${totalOrgs} orgs`;
        }
        return "Loading...";
    })();

    const orgConfigs = revenueContainer.state.revenueConfig.organizations;
    const selectedOrgIds = orgConfigs.map((o) => o.organizationId);
    const selectedOrgNames: Record<string, string> = {};
    orgConfigs.forEach((o) => {
        selectedOrgNames[o.organizationId] = o.organizationName || o.organizationId;
    });

    const totalRevenue = revenueContainer.state.revenueByOrg.reduce(
        (sum, org) => sum + org.totalRevenue,
        0
    );

    return (
        <Container>
            <Tabs selectedIndex={selectedTabIndex} onSelect={handleTabSelection}>
                <PanelHeaderContainer>
                    <TabList className="panel-tab-list">
                        {tabs.map((tab, index) => PanelIconTab({
                            tab: tab,
                            index: index,
                            selectedIndex: selectedTabIndex,
                        }))}
                    </TabList>
                    <TabRowActions>
                        {revenueContainer.state.configSaveError && (
                            <SaveErrorText>{revenueContainer.state.configSaveError}</SaveErrorText>
                        )}
                        <SaveConfigButton
                            onClick={handleSaveConfig}
                            disabled={revenueContainer.state.isSavingConfig}
                        >
                            {revenueContainer.state.isSavingConfig ? "Saving..." : "Save"}
                        </SaveConfigButton>
                    </TabRowActions>
                </PanelHeaderContainer>
                <TabPanel>
                    <TabContent>
                        <Header>
                            <HeaderLeft>
                                <h1>Revenue Calculator</h1>
                                {revenueContainer.state.revenueByOrg.length > 0 && (
                                    <TotalRevenueBadge>
                                        Total: {formatCurrency(totalRevenue)}
                                    </TotalRevenueBadge>
                                )}
                            </HeaderLeft>
                            <HeaderActions>
                                <SettingsButton onClick={() => setShowSettings(!showSettings)}>
                                    {showSettings ? "Hide Settings" : "Settings"}
                                </SettingsButton>
                            </HeaderActions>
                        </Header>

                        {showSettings && (
                            <SettingsPanel>
                                <ConfigExportImportBar>
                                    <ConfigExportImportButton onClick={handleExportCompleteConfig} title="Export entire configuration (all organizations) to JSON file">
                                        Export Complete Config
                                    </ConfigExportImportButton>
                                    <ConfigExportImportButton onClick={handleImportCompleteConfig} title="Import entire configuration from JSON file">
                                        Import Complete Config
                                    </ConfigExportImportButton>
                                </ConfigExportImportBar>
                                <OrganizationSelector
                                    organizationContainer={organizationContainer}
                                    selectedOrgIds={selectedOrgIds}
                                    selectedOrgNames={selectedOrgNames}
                                    onAddOrg={handleAddOrg}
                                    onRemoveOrg={handleRemoveOrg}
                                />
                            </SettingsPanel>
                        )}

                        <ControlsBar>
                            <ControlsLeft>
                                <MonthSelector
                                    selectedMonth={revenueContainer.state.selectedMonth}
                                    onMonthChange={handleMonthChange}
                                    disabled={isLoading}
                                />
                            </ControlsLeft>
                            <ControlsRight>
                                {loadingStatus && (
                                    <LoadingStatus>{loadingStatus}</LoadingStatus>
                                )}
                                {clipboardConfig && (
                                    <ClipboardBadge>
                                        Config copied to clipboard
                                    </ClipboardBadge>
                                )}
                                <RecalcButton
                                    onClick={handleRecalculate}
                                    disabled={isLoading || orgConfigs.length === 0}
                                >
                                    Recalculate
                                </RecalcButton>
                                <LoadDataButton
                                    onClick={handleLoadData}
                                    disabled={isLoading || orgConfigs.length === 0}
                                >
                                    {isLoading ? "Loading..." : "Load Data"}
                                </LoadDataButton>
                                <ForceReloadButton
                                    onClick={handleForceReload}
                                    disabled={isLoading || orgConfigs.length === 0}
                                    title="Bypass cache and reload all engagement data from the API"
                                >
                                    Force Reload
                                </ForceReloadButton>
                            </ControlsRight>
                        </ControlsBar>

                        {revenueContainer.state.errors.length > 0 && (
                            <ErrorsPanel>
                                {revenueContainer.state.errors.map((err, i) => (
                                    <ErrorLine key={i}>{err}</ErrorLine>
                                ))}
                            </ErrorsPanel>
                        )}

                        {orgConfigs.length === 0 ? (
                            <EmptyState>
                                <EmptyTitle>No organizations configured</EmptyTitle>
                                <EmptyDescription>
                                    Click "Settings" above to add organizations you want to track revenue for.
                                    Use the Save button to persist your configuration.
                                </EmptyDescription>
                            </EmptyState>
                        ) : (
                        <OrgSections>
                                {renderGroupedOrgs(
                                    orgConfigs,
                                    revenueContainer,
                                    clipboardConfig,
                                    handleCopyConfig,
                                    collapsedPayingSections,
                                    togglePayingSection
                                )}
                            </OrgSections>
                        )}
                    </TabContent>
                </TabPanel>
                <TabPanel>
                    <TabContent>
                        <ProjectionsTab
                            revenueContainer={revenueContainer}
                        />
                    </TabContent>
                </TabPanel>
            </Tabs>
        </Container>
    );
};

const sortOrgsByName = (orgs: OrganizationConfig[]) =>
    orgs.slice().sort((a, b) => {
        const nameA = (a.organizationName || a.organizationId).toLowerCase();
        const nameB = (b.organizationName || b.organizationId).toLowerCase();
        return nameA.localeCompare(nameB);
    });

const groupOrgsByContractType = (
    orgs: OrganizationConfig[]
): { contractType: ContractType | null; orgs: OrganizationConfig[] }[] => {
    const groups: { contractType: ContractType | null; orgs: OrganizationConfig[] }[] = [];

    // Groups in contract type order, then uncategorized at the end
    for (const ct of CONTRACT_TYPES) {
        const matching = orgs.filter((o) => o.contractType === ct);
        if (matching.length > 0) {
            groups.push({ contractType: ct, orgs: sortOrgsByName(matching) });
        }
    }

    const uncategorized = orgs.filter((o) => !o.contractType);
    if (uncategorized.length > 0) {
        groups.push({ contractType: null, orgs: sortOrgsByName(uncategorized) });
    }

    return groups;
};

const renderGroupedOrgs = (
    orgConfigs: OrganizationConfig[],
    revenueContainer: RevenueContainer,
    clipboardConfig: BillingConfig | null,
    handleCopyConfig: (config: BillingConfig) => void,
    collapsedPayingSections: Set<string>,
    togglePayingSection: (key: string) => void
) => {
    // Split orgs into Paying / Not Paying
    const payingOrgs = orgConfigs.filter((o) => o.paying === true);
    const notPayingOrgs = orgConfigs.filter((o) => o.paying !== true);

    const payingGroups: { key: string; label: string; orgs: OrganizationConfig[] }[] = [];
    if (payingOrgs.length > 0) {
        payingGroups.push({ key: "paying", label: "Paying", orgs: payingOrgs });
    }
    if (notPayingOrgs.length > 0) {
        payingGroups.push({ key: "not-paying", label: "Not Paying", orgs: notPayingOrgs });
    }

    return payingGroups.map((payingGroup) => {
        const isCollapsed = collapsedPayingSections.has(payingGroup.key);
        const contractTypeGroups = groupOrgsByContractType(payingGroup.orgs);
        const groupTotalRevenue = payingGroup.orgs.reduce((sum, orgConfig) => {
            const orgRev = revenueContainer.state.revenueByOrg.find(
                (r) => r.organizationId === orgConfig.organizationId
            );
            return sum + (orgRev?.totalRevenue || 0);
        }, 0);

        return (
            <PayingGroup key={payingGroup.key}>
                <PayingGroupHeader onClick={() => togglePayingSection(payingGroup.key)}>
                    <PayingGroupExpand>{isCollapsed ? "\u25B6" : "\u25BC"}</PayingGroupExpand>
                    <PayingGroupLabel isPaying={payingGroup.key === "paying"}>
                        {payingGroup.label}
                    </PayingGroupLabel>
                    <PayingGroupCount>
                        {payingGroup.orgs.length} org{payingGroup.orgs.length !== 1 ? "s" : ""}
                    </PayingGroupCount>
                    {groupTotalRevenue > 0 && (
                        <PayingGroupRevenue>{formatCurrency(groupTotalRevenue)}</PayingGroupRevenue>
                    )}
                </PayingGroupHeader>

                {!isCollapsed && contractTypeGroups.map((group) => {
                    const groupKey = (payingGroup.key + "__") + (group.contractType || "__uncategorized__");
                    const groupRevenue = group.orgs.reduce((sum, orgConfig) => {
                        const orgRev = revenueContainer.state.revenueByOrg.find(
                            (r) => r.organizationId === orgConfig.organizationId
                        );
                        return sum + (orgRev?.totalRevenue || 0);
                    }, 0);

                    return (
                        <ContractTypeGroup key={groupKey}>
                            <ContractTypeHeader>
                                <ContractTypeLabel>
                                    {group.contractType || "Uncategorized"}
                                </ContractTypeLabel>
                                <ContractTypeCount>
                                    {group.orgs.length} org{group.orgs.length !== 1 ? "s" : ""}
                                </ContractTypeCount>
                                {groupRevenue > 0 && (
                                    <ContractTypeRevenue>
                                        {formatCurrency(groupRevenue)}
                                    </ContractTypeRevenue>
                                )}
                            </ContractTypeHeader>
                            {group.orgs.map((orgConfig) => {
                                const orgRevenue = revenueContainer.state.revenueByOrg.find(
                                    (r) => r.organizationId === orgConfig.organizationId
                                );
                                const orgIsLoading = revenueContainer.state.loadingOrgIds.includes(
                                    orgConfig.organizationId
                                );
                                const isExpanded = revenueContainer.state.expandedOrgs.includes(
                                    orgConfig.organizationId
                                );

                                return (
                                    <OrganizationSection
                                        key={orgConfig.organizationId}
                                        orgConfig={orgConfig}
                                        orgRevenue={orgRevenue}
                                        revenueContainer={revenueContainer}
                                        isExpanded={isExpanded}
                                        isLoading={orgIsLoading}
                                        onToggleExpand={() =>
                                            revenueContainer.toggleOrgExpanded(orgConfig.organizationId)
                                        }
                                        onRemove={() =>
                                            revenueContainer.removeOrganization(orgConfig.organizationId)
                                        }
                                        clipboardConfig={clipboardConfig}
                                        onCopyConfig={handleCopyConfig}
                                    />
                                );
                            })}
                        </ContractTypeGroup>
                    );
                })}
            </PayingGroup>
        );
    });
};

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const Container = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: Muli;

    .react-tabs {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow: hidden;
    }
    .panel-tab-list {
        color: white;
        list-style-type: none;
        display: flex;
        align-items: center;
        margin-left: 28px;
    }
    .react-tabs__tab-panel--selected {
        flex: 1;
        overflow-y: auto;
        min-height: 0;
    }
`;

const TabContent = styled.div`
    padding: 24px;
    max-width: 1600px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h1 {
        margin: 0;
        color: ${color_text_default};
        font-size: 24px;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 8px;
`;

const TotalRevenueBadge = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: ${color_colors_growth};
    background: rgba(88, 189, 153, 0.1);
    padding: 6px 16px;
    border-radius: 6px;
`;

const SettingsButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
        opacity: 0.9;
    }
`;

const SettingsPanel = styled.div`
    background: ${color_shades_lightest};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
`;

const ConfigExportImportBar = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid ${color_shades_light};
`;

const ConfigExportImportButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;

    &:hover {
        opacity: 0.9;
    }
`;

const ControlsBar = styled.div`
    position: sticky;
    top: 0;
    z-index: 10;
    background: ${color_defaults_white};
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    padding: 12px 0;
    margin-top: -12px;
    border-bottom: 1px solid ${color_shades_light};
`;

const ControlsLeft = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-end;
`;

const ControlsRight = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const ClipboardBadge = styled.span`
    font-size: 12px;
    color: ${color_colors_ocean};
    padding: 4px 8px;
    background: rgba(30, 126, 185, 0.08);
    border-radius: 3px;
`;

const LoadingStatus = styled.span`
    font-size: 12px;
    color: ${color_text_light};
    padding: 4px 10px;
    background: ${color_shades_lightest};
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    white-space: nowrap;
`;

const LoadDataButton = styled.button`
    background: ${color_colors_growth};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const RecalcButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ForceReloadButton = styled.button`
    background: transparent;
    color: ${color_text_light};
    border: 1px solid ${color_shades_light};
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;

    &:hover:not(:disabled) {
        color: ${color_colors_decline};
        border-color: ${color_colors_decline};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ErrorsPanel = styled.div`
    background: rgba(216, 65, 77, 0.08);
    border: 1px solid ${color_colors_decline};
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 16px;
`;

const ErrorLine = styled.div`
    font-size: 13px;
    color: ${color_colors_decline};
    margin-bottom: 4px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 60px 24px;
`;

const EmptyTitle = styled.h2`
    color: ${color_text_default};
    margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
    color: ${color_text_light};
    font-size: 14px;
    max-width: 400px;
    margin: 0 auto;
`;

const OrgSections = styled.div``;

const PayingGroup = styled.div`
    margin-bottom: 16px;
`;

const PayingGroupHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: ${color_shades_dark};
    border-radius: 8px;
    cursor: pointer;
    user-select: none;
    margin-bottom: 4px;

    &:hover {
        opacity: 0.9;
    }
`;

const PayingGroupExpand = styled.span`
    font-size: 11px;
    color: ${color_defaults_white};
    opacity: 0.7;
    min-width: 12px;
`;

const PayingGroupLabel = styled.span<{ isPaying: boolean }>`
    font-size: 14px;
    font-weight: 700;
    color: ${color_defaults_white};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex: 1;
`;

const PayingGroupCount = styled.span`
    font-size: 12px;
    color: ${color_defaults_white};
    opacity: 0.7;
`;

const PayingGroupRevenue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${color_colors_growth};
`;

const ContractTypeGroup = styled.div`
    margin-bottom: 8px;
    margin-left: 16px;
`;

const ContractTypeHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0 8px 0;
    margin-bottom: 4px;
    border-bottom: 2px solid ${color_shades_light};
`;

const ContractTypeLabel = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${color_shades_darker};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ContractTypeCount = styled.span`
    font-size: 12px;
    color: ${color_text_light};
`;

const ContractTypeRevenue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${color_colors_growth};
    margin-left: auto;
`;

const TabRowActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
    padding-right: 20px;
`;

const SaveConfigButton = styled.button`
    background: ${color_colors_growth};
    color: ${color_defaults_white};
    border: none;
    padding: 6px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const SaveErrorText = styled.span`
    font-size: 12px;
    color: ${color_colors_decline};
`;

export default Revenue;
